import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";
import { blogPosts } from "@/mocks/products";

const blogCategories = ["All", "Gift Ideas", "Tips & Tricks", "Inspiration", "Corporate", "Products"];
const blogCategoriesF = ["Tous", "Idées Cadeaux", "Conseils & Astuces", "Inspiration", "Entreprise", "Produits"];

export default function BlogPage() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const filtered = activeCategory === "All" || activeCategory === "Tous"
    ? blogPosts
    : blogPosts.filter((p) =>
        isFr ? p.categoryF === activeCategory : p.category === activeCategory
      );

  const [featured, ...rest] = filtered;

  return (
    <div className="min-h-screen bg-[#f5ede0]">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 pb-16 bg-gradient-to-br from-amber-900 via-stone-800 to-amber-800 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20gift%20inspiration%20blog%20header%20with%20flowers%20books%20and%20candles%20on%20cream%20background%2C%20warm%20golden%20light%2C%20premium%20boutique%20aesthetic%2C%20beautiful%20and%20artistic&width=1920&height=400&seq=blog_hero&orientation=landscape"
            alt="Blog"
            className="w-full h-full object-cover object-top opacity-25"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center py-10">
          <span className="inline-block text-amber-300 text-xs font-semibold uppercase tracking-widest mb-3">— Stories & Inspiration —</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("blog_title")}
          </h1>
          <p className="text-white/70 text-lg">{t("blog_subtitle")}</p>
        </div>
      </div>

      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        {/* Category Filter */}
        <div
          className="flex flex-wrap gap-2 mb-10 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)" }}
        >
          {(isFr ? blogCategoriesF : blogCategories).map((cat, i) => (
            <button
              key={i}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-gradient-to-r from-amber-700 to-amber-500 text-white"
                  : "bg-white text-gray-600 border border-amber-100 hover:border-amber-300 hover:text-amber-700"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600">{isFr ? "Aucun article trouvé" : "No articles found"}</p>
          </div>
        ) : (
          <>
            {/* Featured Post */}
            {featured && (
              <div
                className="group relative rounded-3xl overflow-hidden h-[420px] md:h-[500px] mb-10 cursor-pointer transition-all duration-700"
                style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)", transitionDelay: "0.1s" }}
              >
                <img
                  src={featured.image}
                  alt={isFr ? featured.titleF : featured.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#3d1f0a]/90 via-[#3d1f0a]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-amber-600 text-white text-xs font-bold rounded-full uppercase">
                      {isFr ? featured.categoryF : featured.category}
                    </span>
                    <span className="text-white/60 text-xs">{isFr ? featured.dateF : featured.date}</span>
                    <span className="text-white/60 text-xs">{featured.readTime} {t("blog_min_read")}</span>
                  </div>
                  <h2
                    className="text-2xl md:text-3xl font-bold text-white mb-3 max-w-2xl leading-tight"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {isFr ? featured.titleF : featured.title}
                  </h2>
                  <p className="text-white/75 text-sm max-w-xl leading-relaxed mb-4 hidden md:block">
                    {isFr ? featured.excerptF : featured.excerpt}
                  </p>
                  <button className="flex items-center gap-2 text-amber-300 text-sm font-semibold hover:text-white transition-colors cursor-pointer group/btn whitespace-nowrap">
                    {t("blog_read_more")}
                    <i className="ri-arrow-right-line group-hover/btn:translate-x-1 transition-transform"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Rest of Posts */}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((post, i) => (
                  <div
                    key={post.id}
                    className="group bg-white rounded-2xl overflow-hidden border border-amber-100 hover:border-amber-200 hover:-translate-y-2 transition-all duration-400 cursor-pointer"
                    style={{
                      opacity: visible ? 1 : 0,
                      transform: visible ? "translateY(0)" : "translateY(40px)",
                      transition: `all 0.6s ease`,
                      transitionDelay: `${0.2 + i * 0.1}s`,
                    }}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt={isFr ? post.titleF : post.title}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-amber-600 text-white text-xs font-bold rounded-full uppercase">
                          {isFr ? post.categoryF : post.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-gray-400 text-xs">{isFr ? post.dateF : post.date}</span>
                        <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                        <span className="text-gray-400 text-xs">{post.readTime} {t("blog_min_read")}</span>
                      </div>
                      <h3
                        className="font-bold text-gray-800 text-base mb-2 leading-snug group-hover:text-amber-700 transition-colors line-clamp-2"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {isFr ? post.titleF : post.title}
                      </h3>
                      <p className="text-gray-500 text-xs leading-relaxed mb-4 line-clamp-3">
                        {isFr ? post.excerptF : post.excerpt}
                      </p>
                      <button className="flex items-center gap-1.5 text-amber-700 text-xs font-semibold hover:gap-2.5 transition-all cursor-pointer whitespace-nowrap">
                        {t("blog_read_more")}
                        <i className="ri-arrow-right-line text-xs"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Newsletter CTA */}
        <div
          className="mt-16 bg-gradient-to-br from-amber-50 to-[#f5ede0] rounded-3xl p-10 text-center border border-amber-100 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)", transitionDelay: "0.5s" }}
        >
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-600 to-amber-400 mx-auto mb-5">
            <i className="ri-mail-line text-white text-2xl"></i>
          </div>
          <h3 className="text-2xl font-bold text-stone-800 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            {isFr ? "Restez Inspiré" : "Stay Inspired"}
          </h3>
          <p className="text-gray-600 text-sm mb-6 max-w-md mx-auto">
            {isFr
              ? "Abonnez-vous pour recevoir nos dernières idées cadeaux et offres exclusives."
              : "Subscribe to receive our latest gift ideas and exclusive offers."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder={t("footer_newsletter_placeholder")}
              className="flex-1 px-4 py-3 bg-white border border-amber-200 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors"
            />
            <button className="px-6 py-3 bg-gradient-to-r from-amber-700 to-amber-500 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap">
              {t("footer_newsletter_btn")}
            </button>
          </div>
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}